// "Add from an Excel list". Neither picker can express "these six from this team and those
// four from that one" — a spreadsheet can. It feeds the same selection the picker does, so
// the send, its limits and its audit trail are unchanged.

import { Download, Loader2 } from 'lucide-react';
import FileDropZone from '../FileDropZone';
import Modal from '../Modal';
import { UploadErrorBanner } from '../UploadErrorBanner';
import { Spinner } from '../loading';
import type { BannerAudience } from './useBannerAudience';

export function BulkRecipientsModal({ audience }: { audience: BannerAudience }) {
  const {
    showBulkUpload,
    setShowBulkUpload,
    recipientFile,
    parsingRecipients,
    recipientError,
    recipientResult,
    templateLoading,
    downloadRecipientTemplate,
    clearRecipientFile,
    handleRecipientFile,
  } = audience;
  return (
    <Modal isOpen={showBulkUpload} onClose={() => setShowBulkUpload(false)} title="Add from an Excel list">
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <p className="text-xs text-gray-600 leading-relaxed">
            One employee code per row. Everyone found is <strong>added</strong> to the ticks
            already there — nothing already selected is removed, so you can upload more than
            once, or fine-tune by hand afterwards.
          </p>
          <button
            type="button"
            onClick={downloadRecipientTemplate}
            disabled={templateLoading}
            className="flex flex-shrink-0 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {templateLoading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Download className="w-3.5 h-3.5" />
            )}
            Template
          </button>
        </div>

        <FileDropZone
          inputId="announcement-recipients-upload"
          file={recipientFile}
          onFile={handleRecipientFile}
          onClear={clearRecipientFile}
        />

        {parsingRecipients && (
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Spinner />
            Checking the codes against the project roster…
          </div>
        )}

        {recipientError && <UploadErrorBanner message={recipientError} />}

        {recipientResult && (
          <div className="space-y-2">
            <div className="grid grid-cols-4 gap-2">
              {[
                {
                  label: 'Added',
                  value: recipientResult.added ?? 0,
                  tone: 'text-green-700 bg-green-50 border-green-200',
                },
                {
                  label: 'Already ticked',
                  value: recipientResult.matched - (recipientResult.added ?? 0),
                  tone: 'text-gray-700 bg-white border-gray-200',
                },
                {
                  label: 'Duplicates',
                  value: recipientResult.duplicates,
                  tone: 'text-gray-700 bg-white border-gray-200',
                },
                {
                  label: 'Not found',
                  value: recipientResult.invalid,
                  tone:
                    recipientResult.invalid > 0
                      ? 'text-red-700 bg-red-50 border-red-200'
                      : 'text-gray-700 bg-white border-gray-200',
                },
              ].map((tile) => (
                <div
                  key={tile.label}
                  className={`rounded-md border px-2 py-1.5 text-center ${tile.tone}`}
                >
                  <div className="text-base font-semibold leading-tight">{tile.value}</div>
                  <div className="text-[10px] uppercase tracking-wide opacity-70">{tile.label}</div>
                </div>
              ))}
            </div>

            {/* Say which column was read, so a file with the codes somewhere unexpected
                fails visibly rather than quietly reporting "0 found". */}
            {recipientResult.scanned > 0 && (
              <p className="text-xs text-gray-500">
                {recipientResult.hasHeader
                  ? `Read the "${recipientResult.column}" column.`
                  : 'No heading row found — read the first column from row 1.'}
              </p>
            )}

            {recipientResult.errors.length > 0 && (
              <div className="overflow-hidden rounded-md border border-red-200">
                <div className="max-h-40 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-red-50">
                      <tr>
                        <th className="w-14 px-2 py-1.5 text-left font-medium text-red-800">Row</th>
                        <th className="px-2 py-1.5 text-left font-medium text-red-800">Code</th>
                        <th className="px-2 py-1.5 text-left font-medium text-red-800">Problem</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-red-100 bg-white">
                      {recipientResult.errors.map((e, i) => (
                        <tr key={`${e.row}-${i}`}>
                          <td className="px-2 py-1 text-gray-500">{e.row}</td>
                          <td className="px-2 py-1 font-mono text-gray-900">{e.employee_code}</td>
                          <td className="px-2 py-1 text-gray-600">{e.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {recipientResult.errorsTruncated && (
                  <div className="border-t border-red-100 bg-red-50 px-2 py-1.5 text-[11px] text-gray-500">
                    Showing the first {recipientResult.errors.length} of {recipientResult.invalid}.
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
